import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useClientQuery } from 'hooks/apollo';
import { Button } from '@totalsoft/rocket-ui';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { CardActionsContainer } from 'components/common/cards/CardStyles';
import { useTranslation } from 'react-i18next';
import excelTableHeader from 'constants/excelTableHeader';
import { exportToExcel } from 'utils/excel';
import { useToast } from 'hooks/toasts/useToast';
import { useActiveService } from 'hooks/useActiveService';

const defaultMaxSize = 1048575;

const exportTypes = [
  {
    name: 'Excel',
    extension: 'xlsx'
  },
  {
    name: 'CSV',
    extension: 'csv'
  }
];

export const ExportContainer = ({ options, exportHandler, onExport, onCancel, filters = {}, pager = {} }) => {
  const clientQuery = useClientQuery();
  const { activeServiceLoaded } = useActiveService();
  const addToast = useToast();
  const { t } = useTranslation();

  const [exportType, setExportType] = useState(null);
  const [exporting, setExporting] = useState(false);

  const { queryOptions: { query, name, variables: optionVariables, ...queryOptions } = {}, maxSize } = options || {};

  const variables = useMemo(() => {
    if (exportHandler) return null;
    const value = { ...optionVariables };
    return {
      ...value,
      filters: { ...filters, ...value.filters },
      pager: { ...pager, page: 1, size: maxSize || defaultMaxSize, ...value.pager }
    };
  }, [exportHandler, optionVariables, filters, pager, maxSize]);

  const handleExport = useCallback(async () => {
    if (!activeServiceLoaded || !exportType) {
      addToast(t('ListBrowser.ExportTypeRequired'), 'info');
      return;
    }
    setExporting(true);
    if (exportHandler) {
      await exportHandler(exportType);
      setExporting(false);
      onExport && onExport();
      return;
    }
    const { data } = await clientQuery(query, {
      variables,
      fetchPolicy: 'network-only',
      ...queryOptions
    });
    let exportValues = [];
    if (data) {
      const exportDetails = data[name];
      if (Array.isArray(exportDetails)) exportValues = exportDetails;
      else exportValues = exportDetails.values;
    }
    setExporting(false);
    const { formattedList, fileName, sheetName } = excelTableHeader(exportValues, t, name, exportType.extension);
    exportToExcel(formattedList, fileName, sheetName, exportType.extension);
    addToast(t('ListBrowser.Exported'), 'success');
    onExport && onExport();
  }, [activeServiceLoaded, addToast, clientQuery, exportType, exportHandler, name, queryOptions, query, t, variables, onExport]);

  const handleTypeChange = useCallback(type => () => setExportType(type), []);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {exportTypes.map(type => (
          <FormControlLabel
            key={type.name}
            control={<Checkbox checked={exportType?.name === type.name} disabled={exporting} onChange={handleTypeChange(type)} />}
            label={type.name}
          ></FormControlLabel>
        ))}
      </Box>
      <CardActionsContainer sx={{ justifyContent: 'end' }}>
        <Button onClick={handleExport} disabled={exporting}>
          {t('ListBrowser.Export')}
        </Button>
        <Button onClick={onCancel}>{t('Close')}</Button>
      </CardActionsContainer>
    </>
  );
};

ExportContainer.propTypes = {
  options: PropTypes.object,
  exportHandler: PropTypes.func,
  onExport: PropTypes.func,
  onCancel: PropTypes.func,
  filters: PropTypes.object,
  pager: PropTypes.object
};
